import * as actionTypes from "./actionTypes";

import { SESSION } from "../../../../local-storage-constants";
import { socket } from "../../../..";
import { GET_TRANSLATIONS, TRANSLATIONS } from "../../../../redis-constants";
import store from "../../../../store";
import { getBrowserLanguage, updateTranslations } from "../../../sc-utilities/TranslationsUtilities";

const initialState = {
    token: localStorage.getItem(SESSION),
    globalLoading: true,
    expired: false,
};

const loginStart = (state) => {
    return { ...state, loginInProgress: true };
};

const loginSuccess = (state, action) => {
    const defaultLanguage =
        action?.userSettings?.DefaultLanguage || getBrowserLanguage();

    socket.emit(GET_TRANSLATIONS, { LanguageKey: defaultLanguage });
    socket.once(TRANSLATIONS, (message) => store.dispatch(updateTranslations(message.message)));

    // Store the token
    localStorage.setItem(SESSION, action?.token);
    return {
        ...state,
        token: action?.token,
        permissions: action?.permissions,
        refresh: true,
        customers: action?.customers,
        activeCustomer: action?.activeCustomer,
        user: action?.user,
        loginInProgress: false,
        activeExternalServiceAvailable: action?.activeExternalServiceAvailable,
        userSettings: action?.userSettings,
        expiresAt: action?.expiresAt,
        passwordExpired: action?.passwordExpired,
        passwordComplexity: action?.passwordComplexity,
        globalLoading: false
    };
};

const loginFail = (state) => {
    localStorage.clear();
    return {
        ...state,
        loginInProgress: false
    };
};

const logoutStart = (state) => {
    return { ...state, logoutInProgress: true };
};

const logoutFail = (state) => {
    return { ...state, logoutInProgress: false };
};

const logoutSuccess = () => {
    // Remove everything from the localStorage
    localStorage.clear();
    return {
        ...initialState,
        token: null,
        permissions: [],
        activeCustomer: {},
        user: {},
        logoutInProgress: false,
        expiresAt: null,
        globalLoading: false
    };
};

const updateUserStorage = (state, action) => {
    localStorage.setItem(SESSION, action?.userStorage?.Token);
    const user = action?.userStorage?.user?? action?.userStorage;
    const browserLanguage = getBrowserLanguage();
    const defaultLanguage = user?.UserSettings?.DefaultLanguage? user?.UserSettings?.DefaultLanguage : browserLanguage?? "en";
    const userSetting = action?.userStorage?.UserSettings;
    userSetting["DefaultLanguage"] = defaultLanguage;
    
    return {
        ...state,
        globalLoading: false,
        token: action?.userStorage?.Token,
        permissions: action?.userStorage?.permissions,
        customers: action?.userStorage?.customers,
        activeCustomer: action?.userStorage?.activeCustomer,
        user: { ...user, userSetting: userSetting },
        loginInProgress: false,
        activeExternalServiceAvailable: action?.userStorage?.activeExternalServiceAvailable,
        userSettings: action?.userStorage?.UserSettings,
        expiresAt: action?.userStorage?.expiresAt,
        passwordExpired: action?.userStorage?.passwordExpired,
        passwordComplexity: action?.userStorage?.passwordComplexity
    };
};

const updateActiveWarehouse = (state, action) => {
    return {
        ...state,
        user: { ...state.user, ActiveWarehouseID: action.activeWarehouseID },
    };
};


const setPasswordStart = (state) => {
    return {
        ...state
    };
};

const setPasswordSuccess = (state, action) => {
    return {
        ...state,
        user: { ...state.user, PasswordExpires: action.password }
    };
};

const setPasswordFail = (state) => {
    const oldActiveCustomer = state.activeCustomer ? state.activeCustomer : null;
    return {
        ...state,
        activeCustomer: oldActiveCustomer
    };
};

const setActiveCustomerStart = (state) => {
    return {
        ...state
    };
};

const setActiveCustomerSuccess = (state, action) => {
    return {
        ...state,
        activeCustomer: action.activeCustomer,
        permissions: action.permissions,
        activeExternalServiceAvailable: action.activeExternalServiceAvailable
    };
};

const setActiveCustomerFail = (state) => {
    const oldActiveCustomer = state.activeCustomer ? state.activeCustomer : null;
    return {
        ...state,
        activeCustomer: oldActiveCustomer
    };
};

const changeLoggedUserStart = (state) => {
    return {
        ...state
    };
};

const changeLoggedUserSuccess = (state, action) => {
    return {
        ...state,
        user: action.user
    };
};

const changeLoggedUserFail = (state) => {
    return {
        ...state
    };
};

const changeColumnsFilterSettingsStart = state => {
    return {
        ...state
    };
};

const changeColumnsFilterSettingsSuccess = (state, action) => {
    let existingColumns = state.userSettings && typeof state.userSettings.Columns === "object" && state.userSettings !== null ? state.userSettings.Columns : {};

    return {
        ...state,
        userSettings: { ...state.userSettings, Columns: { ...existingColumns, ...action.columns } }
    };
};

const changeColumnsFilterSettingsFail = state => {
    return {
        ...state,
        loading: false
    };
};

const updateActiveCustomerData = (state, action) => {
    return {
        ...state,
        activeCustomer: { ...state.activeCustomer, ...action }
    }
}

const reducer = (state = initialState, action) => {
    switch (action.type) {
        // Login
        case actionTypes.CORE_LOGIN_START:
            return loginStart(state, action);
        case actionTypes.CORE_LOGIN_SUCCESS:
            return loginSuccess(state, action);
        case actionTypes.CORE_LOGIN_FAIL:
            return loginFail(state, action);
        // Logout
        case actionTypes.CORE_LOGOUT_START:
            return logoutStart(state, action);
        case actionTypes.CORE_LOGOUT_SUCCESS:
            return logoutSuccess(state, action);
        case actionTypes.CORE_LOGOUT_FAIL:
            return logoutFail(state, action);
        // Change password
        case actionTypes.USER_CHANGE_PASSWORD_START:
            return setPasswordStart(state, action);
        case actionTypes.USER_CHANGE_PASSWORD_SUCCESS:
            return setPasswordSuccess(state, action);
        case actionTypes.USER_CHANGE_PASSWORD_FAIL:
            return setPasswordFail(state, action);
        // Active customer
        case actionTypes.CORE_SET_ACTIVE_CUSTOMER_START:
            return setActiveCustomerStart(state, action);
        case actionTypes.CORE_SET_ACTIVE_CUSTOMER_SUCCESS:
            return setActiveCustomerSuccess(state, action);
        case actionTypes.CORE_SET_ACTIVE_CUSTOMER_FAIL:
            return setActiveCustomerFail(state, action);
        // Change logged user information
        case actionTypes.CHANGE_LOGGED_USER_INFORMATION_START:
            return changeLoggedUserStart(state, action);
        case actionTypes.CHANGE_LOGGED_USER_INFORMATION_SUCCESS:
            return changeLoggedUserSuccess(state, action);
        case actionTypes.CHANGE_LOGGED_USER_INFORMATION_FAIL:
            return changeLoggedUserFail(state, action);
        // Update column filter
        case actionTypes.CORE_AUTH_SAVE_FILTER_START:
            return changeColumnsFilterSettingsStart(state, action);
        case actionTypes.CORE_AUTH_SAVE_FILTER_SUCCESS:
            return changeColumnsFilterSettingsSuccess(state, action);
        case actionTypes.CORE_AUTH_SAVE_FILTER_FAIL:
            return changeColumnsFilterSettingsFail(state, action);
        // User storage
        case actionTypes.UPDATE_USER_STORAGE:
            return updateUserStorage(state, action);
        // User activeWarehouse
        case actionTypes.HANDLE_ACTIVE_WAREHOUSE:
            return updateActiveWarehouse(state, action);
        case actionTypes.USER_SESSION_EXTENDED:
            return { ...state, expired: false };
        case actionTypes.USER_SESSION_EXPIRED:
            return { ...state, expired: true };
        case "CORE|AUTH|ENABLE_OR_DISABLE_AUTH":
            return { ...state, user: {...state.user, AuthenticationEnabled: !state.user.AuthenticationEnabled}};
        case 'CORE|AUTH|UPDATE_ACTIVE_CUSTOMER_DATA':
            return updateActiveCustomerData(state, action);
        default:
            return state;
    }
};


export default reducer;
